/**
 * CropAdjustModal — manual crop adjustment for the image selector.
 *
 * Used at ad-creation time. The user picks an Approved image, this modal opens
 * with a crop box pre-filled at the smart-crop suggestion (centered, aspect-locked
 * to the placement spec). The user drags/resizes the box, then confirms.
 *
 * **Persistence is deferred.** This modal does NOT call the
 * `POST /media/images/:id/targeted` endpoint — `onConfirm` returns just the
 * source MediaImage and the chosen cropBox. No preview bytes/data URL are
 * produced here; previews are rendered downstream via `CroppedThumbnail`
 * (CSS background positioning over `source.signed_url`). The caller decides
 * when (and whether) to materialize the Targeted MediaImage. PMax materializes
 * at "Upload to Google Ads" click time so abandoned crops never reach GCS;
 * ad creation/edit surfaces materialize inline before passing IDs to their
 * parent form. This is the architecture that keeps GCS clean of half-finished
 * crop iterations.
 *
 * Accessibility mirrors `ConfirmDialog` in part: role=dialog, aria-modal,
 * Escape to close, body scroll lock with scrollbar-width compensation.
 * Full focus management (initial focus, focus trap, focus restore on close)
 * is not yet implemented — tracked as a Layer B polish item.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PixelCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { Button } from '@/components/ui';
import type { MediaImage, CropBox } from '@/types/media';
import type { PlatformPlacementSpec } from '@/types/platformSpecs';
import { toast } from '@/utils';

export interface CropAdjustModalConfirmPayload {
  /** Source image being cropped. Same instance the parent passed in via `source`. */
  source: MediaImage;
  /** Crop box in source-image pixel coordinates. */
  cropBox: CropBox;
}

export interface CropAdjustModalProps {
  isOpen: boolean;
  source: MediaImage | null;
  spec: PlatformPlacementSpec | null;
  onClose: () => void;
  /** Called when the user confirms the crop. The caller is responsible for
   *  whatever happens next (persist via API, store in form state, etc.). */
  onConfirm: (payload: CropAdjustModalConfirmPayload) => void;
}

function buildInitialCrop(
  imageWidth: number,
  imageHeight: number,
  aspect: number
): Crop {
  // Center an aspect-locked crop that fills as much of the image as possible.
  return centerCrop(
    makeAspectCrop({ unit: '%', width: 100 }, aspect, imageWidth, imageHeight),
    imageWidth,
    imageHeight
  );
}

function pixelCropToBox(crop: PixelCrop): CropBox {
  return {
    x: Math.round(crop.x),
    y: Math.round(crop.y),
    w: Math.round(crop.width),
    h: Math.round(crop.height),
  };
}

export function CropAdjustModal({
  isOpen,
  source,
  spec,
  onClose,
  onConfirm,
}: CropAdjustModalProps) {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop | undefined>(undefined);
  const [pixelCrop, setPixelCrop] = useState<PixelCrop | null>(null);

  const aspect = spec?.aspect_ratio_float;

  // Reset crop state whenever the source or spec changes.
  useEffect(() => {
    setCrop(undefined);
    setPixelCrop(null);
  }, [source?.id, spec?.key]);

  // Body scroll lock + Escape handling, mirroring ConfirmDialog (including the
  // scrollbar-width padding compensation so removing the page scrollbar doesn't
  // produce a horizontal layout shift when the modal opens).
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleImageLoad = useCallback(() => {
    if (!imgRef.current || !aspect || !source) return;
    const img = imgRef.current;
    const { naturalWidth, naturalHeight } = img;
    setCrop(buildInitialCrop(naturalWidth, naturalHeight, aspect));

    // Seed `pixelCrop` from the suggested crop too so the Save button is
    // enabled on first paint — without this, ReactCrop only fires onComplete
    // after user interaction, so the "accept the suggested crop" path was
    // gated on the user dragging the box at least once.
    //
    // The percent → source-pixel conversion below cancels through the
    // displayed dimensions algebraically (centered.x% * source.width =
    // source-pixel x), so we don't need to read img.width / getBoundingClientRect
    // for the seed — that's only relevant for `handleCropComplete` where
    // ReactCrop hands us pixel coords in display space.
    const aspectCrop = makeAspectCrop({ unit: '%', width: 100 }, aspect, naturalWidth, naturalHeight);
    const centered = centerCrop(aspectCrop, naturalWidth, naturalHeight);
    setPixelCrop({
      unit: 'px',
      x: (centered.x / 100) * source.width,
      y: (centered.y / 100) * source.height,
      width: (centered.width / 100) * source.width,
      height: (centered.height / 100) * source.height,
    });
  }, [aspect, source]);

  // Convert the displayed crop to source-image pixel coordinates.
  //
  // CRITICAL: scale against `source.width/height` (the persisted full-size dims),
  // NOT `imgRef.naturalWidth/naturalHeight`. The Media Library returns
  // `signed_url` that may point at a thumbnail (per `media_service.build_image_response`
  // when called from list views), so the loaded image's natural dimensions can
  // be smaller than the source. The backend applies `crop_box` to the full-size
  // source bytes, so the box must be in full-size source coordinates regardless
  // of what was shown to the user.
  const handleCropComplete = useCallback(
    (c: PixelCrop) => {
      if (!imgRef.current || !source) return;
      const img = imgRef.current;
      // Use getBoundingClientRect over `img.width` here. `img.width` reads
      // the layout width, but on first paint (before CSS height-constraints
      // fully settle) it can momentarily equal `naturalWidth` — producing a
      // scale of 1.0 instead of the true ratio and a slight horizontal
      // offset when the user accepts the smart-crop suggestion verbatim.
      const rect = img.getBoundingClientRect();
      const displayedW = rect.width || img.width;
      const displayedH = rect.height || img.height;
      const scaleX = source.width / displayedW;
      const scaleY = source.height / displayedH;
      setPixelCrop({
        unit: 'px',
        x: c.x * scaleX,
        y: c.y * scaleY,
        width: c.width * scaleX,
        height: c.height * scaleY,
      });
    },
    [source]
  );

  const handleConfirm = useCallback(() => {
    if (!source || !spec || !pixelCrop) return;
    try {
      onConfirm({ source, cropBox: pixelCropToBox(pixelCrop) });
      // The parent decides when to close. In the multi-image queue flow used
      // by `TargetedImagePicker`, calling `onClose` here would terminate the
      // entire sequence after the first crop — see #1/#7 from PR review.
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save crop';
      toast.error(message);
    }
  }, [source, spec, pixelCrop, onConfirm]);

  const recommendedDimsLabel = useMemo(() => {
    if (!spec) return null;
    const [w, h] = spec.recommended_dims;
    return `${w}×${h}px recommended`;
  }, [spec]);

  if (!isOpen || !source || !spec) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="crop-adjust-title"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        aria-hidden="true"
        onClick={onClose}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4">
            <h2 id="crop-adjust-title" className="text-lg font-semibold text-gray-900">
              Crop for {spec.display_name}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Aspect ratio {spec.aspect_ratio_label}
              {recommendedDimsLabel ? ` · ${recommendedDimsLabel}` : ''}.
              Drag inside the box to reposition, or drag the corners to resize
              — the aspect ratio stays locked. The final image is automatically
              resized to fit the placement spec.
            </p>
          </div>

          <div className="bg-gray-50 rounded p-4 flex items-center justify-center max-h-[60vh] overflow-auto">
            <ReactCrop
              crop={crop}
              onChange={(_pixel, percent) => setCrop(percent)}
              onComplete={handleCropComplete}
              aspect={aspect}
              keepSelection
            >
              <img
                ref={imgRef}
                src={source.signed_url}
                alt={source.alt_text ?? source.original_filename}
                onLoad={handleImageLoad}
                className="max-h-[55vh]"
              />
            </ReactCrop>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={!pixelCrop}
            >
              Save crop
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
